/**
 * The grouped queue — owner rulings DEC-101 and DEC-105, `docs/adr/9042`.
 *
 * Extracted from the alert inbox (ADR 9040) so that every queue reads and acts the same way:
 *
 *   «Raggruppa per»      `GroupByControl` — the dimensions as one segmented control on a "Choose …"
 *                        line (ADR 9036), the choice on the link;
 *   the inbox            `GroupedQueueList` — the totals sentence, then one line per group: the
 *                        platform's first fifty in its order (`cap`), or the planner's ranking of
 *                        every group read (`ranked`, ADR 9034); the CSV/Excel of the set;
 *   the group act        `useGroupAct` + `GroupActControls` + `GroupActOutcome` — the act is
 *                        composed, then a sentence states exactly what will happen with the count
 *                        that is sent as `expectedCount`; a 409 says the set changed, shows the new
 *                        count and asks again; a 422 states the bound and sends nothing more; the
 *                        toast says what the platform APPLIED and what it skipped, in its words.
 *
 * Every word is the queue's own (`GroupActCopy`): this module draws and sequences, and knows no
 * queue. It reads nothing either — the caller passes its read's state and its operation.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useApiMutation } from '../api';
import type { OperationSpec } from '../api/generated/operations';
import { Button, useNotify } from '../primitives';

import { ProblemNotice } from './async';
import { DataTable, type DataColumn } from './data-table';
import { GROUP_LIMIT, groupCurrentCount, groupMaximumCount, isGroupProblem, skippedTotal, type GroupActAnswer } from './group-acts';
import type { RankedCap } from './ranked-cap';
import { ScopeChooser } from './scope-chooser';
import { SentenceLine, type TemplateSentence } from './templates';

// ── «Raggruppa per» ──────────────────────────────────────────────────────────

export interface GroupByControlProps<D extends string> {
  readonly titleKey: string;
  readonly aboutKey?: string;
  readonly dimensions: readonly D[];
  readonly labelKey: Readonly<Record<D, string>>;
  readonly value: D;
  readonly onChoose: (dimension: D) => void;
  /** What the line also asks for — the market a department grouping needs, say. */
  readonly children?: React.ReactNode;
}

/** «Raggruppa per: … · … · …» — one segmented control on a "Choose …" line (ADR 9036). */
export function GroupByControl<D extends string>({
  titleKey, aboutKey, dimensions, labelKey, value, onChoose, children,
}: GroupByControlProps<D>): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <ScopeChooser titleKey={titleKey} {...(aboutKey === undefined ? {} : { aboutKey })}>
      <div role="group" aria-label={t(titleKey)} className="border-ink-primary bg-surface-raised flex border" data-group-by={value}>
        {dimensions.map((dimension) => (
          <button
            key={dimension}
            type="button"
            aria-pressed={value === dimension}
            data-group-by-option={dimension}
            onClick={() => onChoose(dimension)}
            className={`h-[32px] px-3 text-[13px] ${value === dimension ? 'bg-ink-primary text-surface-raised font-semibold' : 'text-ink-primary'}`}
          >
            {t(labelKey[dimension])}
          </button>
        ))}
      </div>
      {children}
    </ScopeChooser>
  );
}

// ── the inbox ────────────────────────────────────────────────────────────────

/** The part of a read's state the list draws. */
export interface GroupedQueueRead<P> {
  readonly isError: boolean;
  readonly error: Error | null;
  readonly data: P | undefined;
  readonly refetch: () => unknown;
}

export interface GroupedQueueListProps<P, G> {
  readonly read: GroupedQueueRead<P>;
  readonly items: (page: P) => readonly G[];
  /** The sentence over the platform's totals — every group the filters match, not the page's. */
  readonly sentence: (page: P) => TemplateSentence;
  readonly columns: readonly DataColumn<G>[];
  readonly rowKey: (group: G) => string;
  readonly captionKey: string;
  /** Where the groups come from — the last paragraph of every column's (i) (DEC-111, ADR 9047). */
  readonly sourceKey: string;
  /** The figures the (i) texts name, from the page's reads. */
  readonly helpValues?: Readonly<Record<string, string | number>>;
  readonly loadFailedKey: string;
  readonly emptyKey: string;
  readonly exportId: string;
  readonly exportTitleKey: string;
  /** The planner's ranking over every group read (ADR 9034). Without it: the platform's first fifty. */
  readonly ranked?: RankedCap<G>;
  /** The guided tour's anchor for the list (`data-tour`, ADR 9044) — on the table and its silhouette. */
  readonly tourAnchor?: string;
}

const GROUP_ROW = 'border-hairline border-b';

/** The table's silhouette while a read is on its way, or while the read is not published. */
export function GroupedQueueGhost<G>({ columns, rowKey, captionKey, tourAnchor }: {
  readonly columns: readonly DataColumn<G>[];
  readonly rowKey: (group: G) => string;
  readonly captionKey: string;
  readonly tourAnchor?: string;
}): React.JSX.Element {
  return (
    <DataTable
      ghost ghostRows={6} rows={[]} rowKey={rowKey} columns={columns} captionKey={captionKey} captionHidden
      {...(tourAnchor === undefined ? {} : { tourAnchor })}
    />
  );
}

/** The inbox: the sentence, then one line per group — or why there is none. */
export function GroupedQueueList<P, G>({
  read, items, sentence, columns, rowKey, captionKey, sourceKey, helpValues, loadFailedKey, emptyKey, exportId, exportTitleKey, ranked,
  tourAnchor,
}: GroupedQueueListProps<P, G>): React.JSX.Element {
  const anchor = tourAnchor === undefined ? {} : { tourAnchor };
  const { t } = useTranslation();
  if (read.isError) return <ProblemNotice error={read.error} titleKey={loadFailedKey} onRetry={() => void read.refetch()} />;
  if (read.data === undefined) return <GroupedQueueGhost columns={columns} rowKey={rowKey} captionKey={captionKey} {...anchor} />;
  const rows = items(read.data);
  return (
    <>
      <SentenceLine sentence={sentence(read.data)} />
      {rows.length === 0 ? (
        <p className="text-body text-ink-secondary" data-groups-empty="" data-tour={tourAnchor}>{t(emptyKey)}</p>
      ) : (
        <DataTable
          rows={rows}
          rowKey={rowKey}
          columns={columns}
          captionKey={captionKey}
          sourceKey={sourceKey}
          {...(helpValues === undefined ? {} : { helpValues })}
          captionHidden
          {...anchor}
          {...(ranked === undefined ? { cap: GROUP_LIMIT } : { ranked })}
          exportId={exportId}
          exportTitleKey={exportTitleKey}
          headerRowClassName="border-hairline border-b text-ink-secondary"
          rowProps={() => ({ className: GROUP_ROW, 'data-group-row': '' } as React.HTMLAttributes<HTMLTableRowElement>)}
        />
      )}
    </>
  );
}

// ── the group act ────────────────────────────────────────────────────────────

/** A queue's words for its acts. Every key is the queue's catalogue's. */
export interface GroupActCopy {
  /** «Rivedi» — composing done, see the sentence. */
  readonly review: string;
  /** Under the sentence: each item still records its own event. */
  readonly eachRecorded: string;
  readonly confirm: string;
  readonly confirmAgain: string;
  readonly back: string;
  /** 409 with a count: `{count}` is the new count. */
  readonly changedStated: string;
  /** 409 without one: the set is re-read; `{count}` is the re-read count. */
  readonly changedRecount: string;
  /** 422: `{count}`, `{maximum}`. */
  readonly tooLarge: string;
  /** Any other refusal's title. */
  readonly failed: string;
  /** One skip reason: `{reason}` (the platform's words), `{count}`. */
  readonly skipReason: string;
  /** The toast's second line: `{count}` skipped, `{reasons}`. */
  readonly skipped: string;
  readonly noneSkipped: string;
}

/** The problem codes a queue's act refuses with. */
export interface GroupActProblems {
  /** 409 — the filter matches other than `expectedCount` now. */
  readonly changed: string;
  /** 422 — the set is above the platform's bound. */
  readonly tooLarge: string;
}

/** The skipped items' reasons, the platform's words verbatim with their counts, as one list. */
export function groupSkipReasons(answer: GroupActAnswer, t: TFunction, language: string, copy: Pick<GroupActCopy, 'skipReason'>): string {
  const parts = answer.skipped
    .filter((entry) => entry.count > 0)
    .map((entry) => t(copy.skipReason, { reason: entry.label.text, count: entry.count }));
  if (parts.length === 0) return '';
  return new Intl.ListFormat(language, { style: 'short', type: 'unit' }).format(parts);
}

/** The toast's second line: what was skipped and why, or that nothing was. */
export function groupSkippedSentence(
  answer: GroupActAnswer, t: TFunction, language: string, copy: Pick<GroupActCopy, 'skipReason' | 'skipped' | 'noneSkipped'>,
): { readonly descriptionKey: string; readonly descriptionValues: Record<string, unknown> } {
  const count = skippedTotal(answer);
  return count === 0
    ? { descriptionKey: copy.noneSkipped, descriptionValues: {} }
    : { descriptionKey: copy.skipped, descriptionValues: { count, reasons: groupSkipReasons(answer, t, language, copy) } };
}

export interface UseGroupActOptions<P, R extends GroupActAnswer> {
  readonly operation: OperationSpec<P, R>;
  /** The count shown beside the act; what is confirmed until a 409 states another. */
  readonly count: number;
  /** The act for a confirmed count, or `null` while something it needs is missing. */
  readonly compose: (expectedCount: number) => P | null;
  /** Never sent: the confirm is disabled until the act is composed. */
  readonly uncomposed: P;
  readonly problems: GroupActProblems;
  /** Re-read what the act changes — after an answer, and after a 409. */
  readonly refresh: () => void;
  /** The toast's title for the answer: a key and its values (`count` is `applied`). */
  readonly done: (answer: R) => { readonly titleKey: string };
  readonly copy: GroupActCopy;
}

export interface GroupActState<P, R> {
  /** The count the planner confirms: the shown count, or the one a 409 stated. */
  readonly expected: number;
  readonly params: P | null;
  readonly confirming: boolean;
  readonly setConfirming: (confirming: boolean) => void;
  readonly changed: 'stated' | 'recount' | null;
  readonly tooLarge: { readonly count: number | null; readonly maximum: number | null } | null;
  readonly outcome: R | null;
  readonly pending: boolean;
  /** The last refusal that is neither a 409 nor a 422 — shown as the queue's `failed`. */
  readonly failure: Error | null;
  readonly fire: () => void;
}

/**
 * The act's lifecycle: compose → confirm the count → one request. The count in the sentence is
 * the `expectedCount` sent; a 409 replaces it with the platform's (or re-reads it) and asks again;
 * nothing is retried by itself.
 */
export function useGroupAct<P, R extends GroupActAnswer>({
  operation, count, compose, uncomposed, problems, refresh, done, copy,
}: UseGroupActOptions<P, R>): GroupActState<P, R> {
  const { t, i18n } = useTranslation();
  const notify = useNotify();
  const [confirming, setConfirming] = useState(false);
  const [expected, setExpected] = useState(count);
  const [changed, setChanged] = useState<'stated' | 'recount' | null>(null);
  const [outcome, setOutcome] = useState<R | null>(null);
  const [tooLarge, setTooLarge] = useState<GroupActState<P, R>['tooLarge']>(null);

  // A 409 that named no count: the set is re-read and its new count is what is confirmed next.
  useEffect(() => {
    if (changed === 'recount') setExpected(count);
  }, [changed, count]);

  const params = compose(expected);
  const mutation = useApiMutation(operation, params ?? uncomposed);
  const failure = mutation.isError
    && !isGroupProblem(mutation.error, 409, problems.changed)
    && !isGroupProblem(mutation.error, 422, problems.tooLarge)
    ? mutation.error
    : null;

  const fire = (): void => {
    setChanged(null);
    setTooLarge(null);
    mutation.mutate(undefined, {
      onSuccess: (result) => {
        const answer = result as R;
        setOutcome(answer);
        refresh();
        notify({
          titleKey: done(answer).titleKey,
          titleValues: { count: answer.applied },
          ...groupSkippedSentence(answer, t, i18n.language, copy),
        });
      },
      onError: (error) => {
        if (isGroupProblem(error, 422, problems.tooLarge)) {
          setTooLarge({ count: groupCurrentCount(error), maximum: groupMaximumCount(error) });
          return;
        }
        if (!isGroupProblem(error, 409, problems.changed)) return;
        const stated = groupCurrentCount(error);
        refresh();
        if (stated === null) setChanged('recount');
        else {
          setExpected(stated);
          setChanged('stated');
        }
      },
    });
  };

  return {
    expected, params, confirming, setConfirming, changed, tooLarge, outcome, pending: mutation.isPending, failure, fire,
  };
}

/**
 * Under the act's inputs: the 409's words, «Rivedi» or the confirm sentence and its two buttons,
 * the 422's words, any other refusal.
 */
export function GroupActControls<P, R>({ state, copy, sentence, destructive = false, reviewDisabled = false }: {
  readonly state: GroupActState<P, R>;
  readonly copy: GroupActCopy;
  /** The confirm sentence for the composed act, or `null` while it is not composed. */
  readonly sentence: { readonly key: string; readonly values: Record<string, string | number> } | null;
  readonly destructive?: boolean;
  /** Something the act needs is not valid yet, beyond being composed. */
  readonly reviewDisabled?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      {state.changed !== null && (
        <p role="alert" data-group-changed="" className="text-body text-status-critical-strong max-w-prose">
          {t(state.changed === 'stated' ? copy.changedStated : copy.changedRecount, { count: state.expected })}
        </p>
      )}

      {!state.confirming ? (
        <div>
          <Button variant="primary" size="sm" disabled={state.params === null || reviewDisabled} onClick={() => state.setConfirming(true)} data-group-act-next="">
            {t(copy.review)}
          </Button>
        </div>
      ) : (
        <div className="border-ink-primary flex flex-col gap-3 border-t-2 pt-3" data-group-confirm="">
          {sentence !== null && (
            <p className="text-body text-ink-primary font-semibold" data-group-confirm-sentence="">
              {t(sentence.key, sentence.values)}
            </p>
          )}
          <p className="text-caption text-ink-secondary">{t(copy.eachRecorded)}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={destructive ? 'destructive' : 'primary'}
              size="sm"
              disabled={state.params === null || state.pending}
              loading={state.pending}
              onClick={state.fire}
              data-group-act-confirm=""
            >
              {t(state.changed === null ? copy.confirm : copy.confirmAgain)}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => state.setConfirming(false)}>{t(copy.back)}</Button>
          </div>
        </div>
      )}

      {state.tooLarge !== null && (
        <p role="alert" data-group-too-large="" className="text-body text-status-critical-strong max-w-prose">
          {t(copy.tooLarge, { count: state.tooLarge.count ?? state.expected, maximum: state.tooLarge.maximum ?? 0 })}
        </p>
      )}

      {state.failure !== null && <ProblemNotice error={state.failure} titleKey={copy.failed} />}
    </>
  );
}

/** The drawer's answer once the platform answered: what it applied, and what it skipped. */
export function GroupActOutcome({ answer, titleKey, copy, onClose }: {
  readonly answer: GroupActAnswer;
  readonly titleKey: string;
  readonly copy: GroupActCopy;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const skipped = groupSkippedSentence(answer, t, i18n.language, copy);
  return (
    <div className="flex flex-col gap-3" data-group-outcome="">
      <p className="text-body text-ink-primary flex flex-wrap gap-x-3">
        <span className="font-semibold">{t(titleKey, { count: answer.applied })}</span>
        <span>{t(skipped.descriptionKey, skipped.descriptionValues)}</span>
      </p>
      <div><Button size="sm" variant="secondary" onClick={onClose}>{t('action.close')}</Button></div>
    </div>
  );
}
